import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthContext';
import { useTicketComments, useCreateComment } from '@/lib/adminApi';
import type { TicketCommentSummary } from '@ventin/shared';

/**
 * Two-way ticket comment thread (plan §4.4 / D9).
 *
 * Visibility is enforced server-side: CLIENT callers never receive
 * `isInternal` notes, so this component is safe on both the portal and admin
 * surfaces. `canPostInternal` (admin/support only) reveals the internal-note
 * toggle and renders internal notes visually distinct.
 */
export interface CommentThreadProps {
  ticketId: string;
  /** ADMIN/SUPPORT: enable posting internal notes. Never pass true for CLIENT. */
  canPostInternal?: boolean;
}

export function CommentThread({
  ticketId,
  canPostInternal = false,
}: CommentThreadProps): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading, isError } = useTicketComments(ticketId);
  const createMut = useCreateComment();

  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const comments = data?.comments ?? [];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed.length === 0) return;
    try {
      await createMut.mutateAsync({
        id: ticketId,
        input: { body: trimmed, ...(canPostInternal && isInternal ? { isInternal: true } : {}) },
      });
      setBody('');
      setIsInternal(false);
    } catch {
      toast.error("Couldn't post comment — try again");
    }
  }

  return (
    <section className="rounded-md border bg-background p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        Comments
      </h3>

      {isLoading && <p className="text-sm text-muted-foreground">Loading comments…</p>}
      {isError && <p className="text-sm text-destructive">Couldn&apos;t load comments.</p>}

      {!isLoading && !isError && (
        <ul className="space-y-3 mb-4">
          {comments.length === 0 && (
            <li className="text-sm text-muted-foreground">No comments yet.</li>
          )}
          {comments.map((c) => (
            <CommentItem key={c.id} comment={c} currentUserId={user?.id ?? null} />
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          aria-label="Add a comment"
          placeholder={
            canPostInternal && isInternal ? 'Internal note (staff only)…' : 'Add a comment…'
          }
          value={body}
          maxLength={20000}
          rows={3}
          onChange={(e) => setBody(e.target.value)}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <div className="flex items-center justify-between gap-3">
          {canPostInternal ? (
            <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
              Internal note (not visible to client)
            </label>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            size="sm"
            disabled={body.trim().length === 0 || createMut.isPending}
          >
            {createMut.isPending ? 'Posting…' : 'Post comment'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function CommentItem({
  comment,
  currentUserId,
}: {
  comment: TicketCommentSummary;
  currentUserId: string | null;
}): JSX.Element {
  const author =
    comment.authorId === null
      ? 'System'
      : comment.authorId === currentUserId
        ? 'You'
        : comment.isInternal
          ? 'Team'
          : 'Support';

  return (
    <li
      className={cn(
        'rounded-md border p-3 text-sm',
        comment.isInternal ? 'border-amber-200 bg-amber-50' : 'bg-background',
      )}
    >
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{author}</span>
        {comment.isInternal && (
          <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-900">
            Internal note
          </span>
        )}
        <span>{new Date(comment.createdAt).toLocaleString()}</span>
      </div>
      <p className="whitespace-pre-wrap">{comment.body}</p>
    </li>
  );
}
